import React from 'react';
import {Helmet} from "react-helmet";
import { Box, Container, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { getUTCTimeStamp } from '../utilities/helper';

const TermAndConditionPage: React.FC = () => {

  return (
    <>
      <Helmet>
        <title>Terms & Conditions - Scrap E-Auction Online Scrap Metal Auction Platform</title>
        <meta name="description" content="Terms and conditions for using Scrap E-Auction platform" />
      </Helmet>
      <Box sx={{ backgroundColor: '#fbf9fa'}}>
        <Container sx={{ py: 4 }}>
          <Stack gap={5} direction="column">
            <Box sx={{ mt: 4, textAlign: 'left' }}>
              <Typography variant="h1" gutterBottom sx={{ fontWeight: '600' }}>
                Terms & Conditions
              </Typography>
              <Typography variant="body1" gutterBottom color="textSecondary">
                Last updated: {getUTCTimeStamp().format('DD/MM/YYYY')}
              </Typography>
            </Box>
            <Paper sx={{ p: 4 }}>
              <Stack gap={5} direction="column">
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >1. Acceptance of Terms</Typography>
                  <Typography variant="body1" component="p">
                    By accessing and using Steel E-Auction platform, you accept and agree to be bound by these 
                    Terms and Conditions. If you do not agree to these terms, please do not use our platform.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >2. User Registration</Typography>
                  <Typography variant="body1" component="p">
                    To participate in auctions, users must:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Provide accurate and complete registration information" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Maintain valid business credentials including PAN and GSTIN" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Undergo admin verification and approval" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Keep account information confidential and secure" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >3. Auction Participation</Typography>
                  <Typography variant="body1" component="p">
                    All auction participants must:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Be approved by admin before placing bids" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Honor all winning bids" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Complete transactions as per agreed terms" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Not engage in bid manipulation or collusion" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >4. Seller Obligations</Typography>
                  <Typography variant="body1" component="p">
                    Sellers (Companies) must:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Provide accurate product descriptions and specifications" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Honor the auction results and sell to the winning bidder" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Deliver materials as described in the auction listing" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Comply with all applicable laws and regulations" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >5. Buyer Obligations</Typography>
                  <Typography variant="body1" component="p">
                    Buyers must:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Pay for winning bids as per agreed payment terms" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Arrange for material collection within specified timeframes" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Provide required documentation for verification" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Not place frivolous or non-serious bids" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >6. Platform Fees</Typography>
                  <Typography variant="body1" component="p">
                    Platform usage fees, if any, will be clearly communicated to users. The platform reserves the 
                    right to modify fees with prior notice to users.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >7. Prohibited Activities</Typography>
                  <Typography variant="body1" component="p">
                  Users must not:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Engage in fraudulent activities" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Manipulate auction results" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Share account credentials" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Attempt to circumvent platform security" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Use automated bidding bots (except platform-provided auto-bid)" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >8. Dispute Resolution</Typography>
                  <Typography variant="body1" component="p">
                    Any disputes arising from transactions must first be resolved between buyers and sellers. 
                    The platform may provide mediation support but is not responsible for transaction disputes.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >9. Limitation of Liability</Typography>
                  <Typography variant="body1" component="p">
                  Steel E-Auction acts as a facilitator and is not responsible for:
                  </Typography>
                  <List sx={{ listStyleType: "disc", pl: 4 }}>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Quality, quantity, or condition of materials" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Payment disputes between parties" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Delivery or logistics issues" />
                    </ListItem>
                    <ListItem sx={{ display: "list-item", py: 0 }}>
                      <ListItemText primary="Acts of God or force majeure events" />
                    </ListItem>
                  </List>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >10. Termination</Typography>
                  <Typography variant="body1" component="p">
                    We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activities.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >11. Changes to Terms</Typography>
                  <Typography variant="body1" component="p">
                  We may update these terms from time to time. Continued use of the platform after changes constitutes acceptance of the new terms.
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  <Typography variant="h3" fontWeight={600} >12. Governing Law</Typography>
                  <Typography variant="body1" component="p">
                    These terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of courts in India.
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default TermAndConditionPage;
